"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatabaseProjectionBudgetError = exports.DATABASE_PROJECTION_RESULT_BUDGET_BYTES = void 0;
exports.assertDatabaseProjectionSerializable = assertDatabaseProjectionSerializable;
exports.normalizeDatabaseProjectionFilter = normalizeDatabaseProjectionFilter;
exports.createDatabaseProjectionAccumulator = createDatabaseProjectionAccumulator;
const database_large_data_policy_1 = require("./database-large-data-policy");
const database_projection_filter_1 = require("./database-projection-filter");
const database_projection_groups_1 = require("./database-projection-groups");
const database_projection_sort_1 = require("./database-projection-sort");
const database_search_1 = require("./database-search");
const database_view_filter_1 = require("./database-view-filter");
__exportStar(require("./database-projection-filter"), exports);
__exportStar(require("./database-projection-groups"), exports);
__exportStar(require("./database-projection-metadata"), exports);
__exportStar(require("./database-projection-sort"), exports);
__exportStar(require("./database-projection-types"), exports);
exports.DATABASE_PROJECTION_RESULT_BUDGET_BYTES = database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.projection.resultBudgetBytes;
class DatabaseProjectionBudgetError extends Error {
    constructor(limitBytes, retainedBytes) {
        super('Database projection exceeds its result budget');
        this.limitBytes = limitBytes;
        this.retainedBytes = retainedBytes;
        this.code = 'DATABASE_PROJECTION_BUDGET_EXCEEDED';
    }
}
exports.DatabaseProjectionBudgetError = DatabaseProjectionBudgetError;
/** Serial metadata must never carry live docs, renderers, functions or cycles. */
function assertDatabaseProjectionSerializable(value, seen = new Set()) {
    if (value == null ||
        typeof value === 'string' ||
        typeof value === 'boolean' ||
        typeof value === 'undefined')
        return;
    if (typeof value === 'number' && Number.isFinite(value))
        return;
    if (typeof value !== 'object' || seen.has(value))
        throw new TypeError('Unsupported database projection metadata');
    const prototype = Object.getPrototypeOf(value);
    if (!Array.isArray(value) &&
        prototype !== Object.prototype &&
        prototype !== null)
        throw new TypeError('Unsupported database projection metadata');
    seen.add(value);
    for (const child of Object.values(value))
        assertDatabaseProjectionSerializable(child, seen);
    seen.delete(value);
}
function normalizeDatabaseProjectionFilter(filter) {
    const diagnostics = [];
    const visit = (value, path) => {
        if (value.type === 'group') {
            value.conditions.forEach((child, index) => visit(child, `${path}.conditions.${index}`));
            return;
        }
        const name = value.function;
        if (!name || !(0, database_projection_filter_1.isDatabaseProjectionFilterOperator)(name))
            diagnostics.push({
                code: 'unsupported-operator',
                path: `${path}.function`,
                message: `Unsupported projection filter operator "${name ?? ''}".`,
            });
        if (name && (0, database_projection_filter_1.isDatabaseProjectionFilterOperator)(name)) {
            const requiredArgs = name === 'olderThanDays'
                ? 1
                : Math.max(0, ((0, database_view_filter_1.getDatabaseViewFilterFunction)(name)?.length ??
                    (name === 'isTrue' || name === 'isFalse' ? 1 : 2)) - 1);
            if (value.args.length < requiredArgs ||
                value.args.slice(0, requiredArgs).some(arg => arg.value === undefined))
                diagnostics.push({
                    code: 'unsupported-operator',
                    path: `${path}.args`,
                    message: 'Projection filter arguments are incomplete.',
                });
        }
        if (name === 'olderThanDays' &&
            (!Number.isSafeInteger(value.args[0]?.value) ||
                value.args[0]?.value <= 0))
            diagnostics.push({
                code: 'unsupported-operator',
                path: `${path}.args`,
                message: 'Projection age requires positive whole days.',
            });
        try {
            assertDatabaseProjectionSerializable(value.args);
        }
        catch {
            diagnostics.push({
                code: 'unsupported-operator',
                path: `${path}.args`,
                message: 'Projection filter arguments are not serializable.',
            });
        }
    };
    visit(filter, 'filter');
    return {
        filter: diagnostics.length ? null : filter,
        diagnostics,
    };
}
/** One source chunk at a time; no source-sized cell/row array is retained. */
function createDatabaseProjectionAccumulator(input) {
    const { query, columns, nowMs, maxResultBytes, viewState } = input;
    if (!Number.isSafeInteger(maxResultBytes) ||
        maxResultBytes <= 0 ||
        !Number.isFinite(nowMs))
        throw new TypeError('Invalid database projection budget or reference time');
    assertDatabaseProjectionSerializable([query, columns, viewState]);
    const encoder = new TextEncoder();
    const bytes = (value) => encoder.encode(JSON.stringify(value) ?? 'null').byteLength;
    let retainedBytes = bytes([query, columns, viewState]) + 256;
    let failure;
    const account = (delta) => {
        const next = retainedBytes + delta;
        if (next > maxResultBytes) {
            failure = new DatabaseProjectionBudgetError(maxResultBytes, next);
            throw failure;
        }
        retainedBytes = next;
    };
    account(0);
    const diagnostics = [];
    const diagnosticKeys = new Set();
    const appendDiagnostics = (items) => {
        for (const diagnostic of items) {
            const key = `${diagnostic.code}:${diagnostic.path ?? ''}`;
            if (diagnosticKeys.has(key))
                continue;
            account(bytes(diagnostic) + 32);
            diagnosticKeys.add(key);
            diagnostics.push(diagnostic);
        }
    };
    const normalizedFilter = query.filter
        ? normalizeDatabaseProjectionFilter(query.filter)
        : undefined;
    appendDiagnostics(normalizedFilter?.diagnostics ?? []);
    const evaluateFilter = normalizedFilter?.filter
        ? (0, database_projection_filter_1.createDatabaseProjectionFilterEvaluator)(columns, normalizedFilter.filter, { nowMs })
        : undefined;
    const sorts = [
        ...(query.viewSort?.sortBy.map(sort => ({
            propertyId: sort.ref.name,
            type: 'property',
            direction: sort.desc ? 'desc' : 'asc',
        })) ?? []),
        ...(query.sort ?? []),
    ];
    for (const sort of sorts)
        if (sort.type !== 'property')
            appendDiagnostics([
                {
                    code: 'unsupported-sort',
                    path: 'sort.type',
                    message: `Unsupported projection sort "${String(sort.type)}".`,
                },
            ]);
    for (const request of query.aggregates ?? [])
        if (request.type !== 'count' && !request.propertyId)
            appendDiagnostics([
                {
                    code: 'unsupported-aggregate',
                    path: `aggregates.${request.id}`,
                    message: 'Projection aggregate requires a propertyId.',
                },
            ]);
    const windowDiagnostics = [];
    const initialWindow = (0, database_search_1.normalizeDatabaseProjectionWindow)(query.window, Number.MAX_SAFE_INTEGER, windowDiagnostics);
    appendDiagnostics(windowDiagnostics);
    const keep = query.window
        ? initialWindow.offset + initialWindow.limit
        : Number.MAX_SAFE_INTEGER;
    const columnMap = new Map(columns.map(column => [column.id, column]));
    const propertyIds = columns.map(column => column.id);
    const manual = new Map(query.viewSort?.manuallySort.map((id, index) => [id, index]) ?? []);
    const rank = (row, ranks) => ranks.get(row.id) ?? Number.MAX_SAFE_INTEGER;
    // The view sorts, then the query sorts, each column by its type; the keys
    // of a retained row are its cells as the view keeps them.
    const viewOrder = (0, database_projection_sort_1.databaseRowViewOrder)(sorts, columns);
    const compare = (left, right, ranks) => (ranks !== manual ? rank(left, ranks) - rank(right, ranks) : 0) ||
        viewOrder.compare(left.keys, right.keys) ||
        rank(left, ranks) - rank(right, ranks) ||
        left.order - right.order;
    const addBounded = (rows, row, ranks) => {
        // A single over-budget sort key is rejected even if it would miss the window.
        if (row.bytes > maxResultBytes) {
            failure = new DatabaseProjectionBudgetError(maxResultBytes, row.bytes);
            throw failure;
        }
        if (!keep)
            return;
        let low = 0, high = rows.length;
        while (low < high) {
            const mid = (low + high) >>> 1;
            if (compare(rows[mid], row, ranks) <= 0)
                low = mid + 1;
            else
                high = mid;
        }
        if (low >= keep)
            return;
        const displaced = rows.length >= keep ? rows[rows.length - 1] : undefined;
        account(row.bytes - (displaced?.bytes ?? 0));
        rows.splice(low, 0, row);
        if (rows.length > keep)
            rows.pop();
    };
    const isGroupList = (value) => Array.isArray(value);
    const groupBys = query.groupBy
        ? isGroupList(query.groupBy)
            ? query.groupBy
            : [query.groupBy]
        : [];
    const enabledGroups = new Set();
    const groups = new Map();
    const ensureGroup = (columnId, name, key, value) => {
        const identity = JSON.stringify([columnId, name, key]);
        let group = groups.get(identity);
        if (!group) {
            const state = viewState.groups.find(state => state.columnId === columnId && state.name === name);
            const cardSort = state?.properties.find(property => property.key === key)
                ?.manuallyCardSort ?? [];
            const summary = {
                columnId,
                name,
                key,
                value,
                totalCount: 0,
                totalKnown: false,
                complete: false,
                rowIds: [],
                window: initialWindow,
            };
            account(bytes(summary) + bytes(identity) + bytes(cardSort) + 64);
            group = {
                summary,
                rows: [],
                manual: cardSort.length
                    ? new Map(cardSort.map((id, index) => [id, index]))
                    : manual,
            };
            groups.set(identity, group);
        }
        return group;
    };
    for (const groupBy of groupBys) {
        const column = columnMap.get(groupBy.columnId);
        if (!column) {
            appendDiagnostics([
                {
                    code: 'unsupported-operator',
                    path: 'groupBy.columnId',
                    message: 'Unknown projection group property.',
                },
            ]);
            continue;
        }
        try {
            (0, database_projection_groups_1.databaseProjectionGroupValues)(column, groupBy, null, nowMs);
            enabledGroups.add(JSON.stringify([column.id, groupBy.name]));
            for (const group of (0, database_projection_groups_1.databaseProjectionDefaultGroups)(column, groupBy.name))
                ensureGroup(column.id, groupBy.name, group.key, group.value);
        }
        catch (error) {
            if (error instanceof DatabaseProjectionBudgetError)
                throw error;
            appendDiagnostics([
                {
                    code: 'unsupported-operator',
                    path: 'groupBy.name',
                    message: String(error),
                },
            ]);
        }
    }
    const aggregates = (query.aggregates ?? []).map(request => ({
        request,
        state: (0, database_search_1.createDatabaseProjectionAggregate)(request),
    }));
    account(aggregates.length * 128);
    const rows = [];
    let totalCount = 0;
    const searchPredicate = query.searchText?.trim()
        ? [{ type: 'text-search', query: query.searchText.trim() }]
        : [];
    const predicates = [...searchPredicate, ...(query.predicates ?? [])].map(predicate => (0, database_search_1.prepareDatabaseProjectionPredicate)(predicate, propertyIds));
    const preparation = {
        textPredicates: predicates.reduce((n, predicate) => n + predicate.work.textPredicates, 0),
        cellBatches: predicates.reduce((n, predicate) => n + predicate.work.cellBatches, 0),
    };
    return {
        preparation,
        add(chunk) {
            if (failure)
                throw failure;
            for (const row of chunk) {
                const cellCache = new Map();
                const cell = (id) => {
                    if (cellCache.has(id))
                        return cellCache.get(id);
                    const raw = row.readCell ? row.readCell(id) : row.cells[id];
                    const column = columnMap.get(id);
                    const value = column
                        ? (0, database_projection_groups_1.normalizeDatabaseProjectionCell)(raw, column)
                        : raw;
                    cellCache.set(id, value);
                    return value;
                };
                if (evaluateFilter && !evaluateFilter(cell))
                    continue;
                if (query.filter && !normalizedFilter?.filter)
                    continue;
                const localDiagnostics = [];
                const matches = predicates.every(predicate => predicate.matches(cell, localDiagnostics, id => {
                    const column = columnMap.get(id);
                    const value = cell(id);
                    return column
                        ? (0, database_projection_groups_1.databaseProjectionDisplayValue)(value, column)
                        : value;
                }, row.prepareCells));
                appendDiagnostics(localDiagnostics);
                if (!matches)
                    continue;
                totalCount += 1;
                for (const aggregate of aggregates)
                    aggregate.state.add(aggregate.request.propertyId
                        ? cell(aggregate.request.propertyId)
                        : row.id);
                const keys = Object.fromEntries(sorts.map(sort => [sort.propertyId, cell(sort.propertyId)]));
                assertDatabaseProjectionSerializable(keys);
                const entry = {
                    id: row.id,
                    order: row.order,
                    keys,
                    bytes: bytes([row.id, row.order, keys]) + 32,
                };
                addBounded(rows, entry, manual);
                for (const groupBy of groupBys) {
                    const column = columnMap.get(groupBy.columnId);
                    if (!column ||
                        !enabledGroups.has(JSON.stringify([column.id, groupBy.name])))
                        continue;
                    for (const value of (0, database_projection_groups_1.databaseProjectionGroupValues)(column, groupBy, cell(column.id), nowMs)) {
                        const group = ensureGroup(column.id, groupBy.name, value.key, value.value);
                        group.summary.totalCount += 1;
                        addBounded(group.rows, entry, group.manual);
                    }
                }
            }
        },
        finish(sourceComplete) {
            if (failure)
                throw failure;
            const valid = diagnostics.length === 0;
            const complete = sourceComplete && valid;
            const resultDiagnostics = sourceComplete
                ? diagnostics
                : [
                    ...diagnostics,
                    {
                        code: 'partial-source',
                        message: 'Projection source is partial; result must not be rendered as complete.',
                    },
                ];
            const window = (0, database_search_1.normalizeDatabaseProjectionWindow)(query.window, totalCount, []);
            const summaries = [];
            for (const groupBy of groupBys) {
                const column = columnMap.get(groupBy.columnId);
                if (!column)
                    continue;
                const state = viewState.groups.find(state => state.columnId === groupBy.columnId && state.name === groupBy.name);
                const ranks = new Map(state?.properties.map((property, index) => [property.key, index]) ??
                    []);
                const current = [...groups.values()].filter(group => group.summary.columnId === column.id &&
                    group.summary.name === groupBy.name);
                const inputOrder = new Map(current.map((group, index) => [group.summary.key, index]));
                current.sort((left, right) => {
                    const l = left.summary, r = right.summary;
                    const mode = state?.sortMode ??
                        (groupBy.sort ? (groupBy.sort.desc ? 'desc' : 'asc') : 'default');
                    if (groupBy.name.startsWith('date-')) {
                        const dateOrder = (0, database_projection_groups_1.compareDatabaseProjectionDateGroupKeys)(groupBy.name, l.key, r.key, mode !== 'desc', nowMs);
                        if (dateOrder && mode !== 'default')
                            return dateOrder;
                        if (mode === 'default' && !ranks.size)
                            return dateOrder;
                    }
                    if (mode !== 'default') {
                        const logicalName = groupBy.name === 'select' || groupBy.name === 'multi-select'
                            ? 'Tag'
                            : groupBy.name === 'number'
                                ? 'Number'
                                : groupBy.name === 'boolean'
                                    ? 'Boolean'
                                    : 'String';
                        const valueOrder = (0, database_projection_sort_1.compareDatabaseProjectionTypedValues)({ name: logicalName, data: (0, database_projection_groups_1.databaseProjectionOptions)(column) }, l.value, r.value, mode === 'desc');
                        if (valueOrder)
                            return valueOrder;
                    }
                    const rankOrder = (ranks.get(l.key) ?? Number.MAX_SAFE_INTEGER) -
                        (ranks.get(r.key) ?? Number.MAX_SAFE_INTEGER);
                    return (rankOrder ||
                        (mode === 'default' && groupBy.name.startsWith('date-')
                            ? (0, database_projection_groups_1.compareDatabaseProjectionDateGroupKeys)(groupBy.name, l.key, r.key, true, nowMs)
                            : 0) ||
                        inputOrder.get(l.key) - inputOrder.get(r.key));
                });
                for (const group of current) {
                    const groupWindow = (0, database_search_1.normalizeDatabaseProjectionWindow)(query.window, group.summary.totalCount, []);
                    summaries.push({
                        ...group.summary,
                        complete,
                        totalKnown: complete,
                        window: groupWindow,
                        rowIds: group.rows
                            .slice(groupWindow.offset, groupWindow.offset + groupWindow.limit)
                            .map(row => row.id),
                    });
                }
            }
            const page = {
                rowIds: rows
                    .slice(window.offset, window.offset + window.limit)
                    .map(row => row.id),
                totalCount,
                totalKnown: complete,
                window,
                groups: summaries,
                aggregates: aggregates.map(aggregate => aggregate.state.result(complete)),
                diagnostics: resultDiagnostics.slice(),
                complete,
                freshness: 'snapshot',
                source: 'remote-scan',
                syncState: valid
                    ? sourceComplete
                        ? 'live'
                        : 'partial'
                    : 'unsupported',
            };
            const outputBytes = bytes(page);
            if (outputBytes > maxResultBytes)
                throw new DatabaseProjectionBudgetError(maxResultBytes, outputBytes);
            return page;
        },
    };
}
